// Sistem Ayarları — alt menü ana sidebar'da (AppShell expand). Sol-rail nav kaldırıldı.
// Route: /admin/settings/:section → llm | mail | prompt | budget | ui | evidence | audit.

import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type {
  AuditLogResponse,
  LLMTestResponse,
  MailTestResponse,
  ProjectSnapshotResponse,
  ProjectsListResponse,
  PromptPreviewResponse,
  SettingListResponse,
  SettingRegistryEntry,
} from "@mahir/shared";
import { formatReportingPeriod, PLACEHOLDER_CATALOG, SETTING_REGISTRY } from "@mahir/shared";
import { api } from "../../api/client";
import { EvidencePolicyPanel } from "../../components/admin/EvidencePolicyPanel";

type TabKey = "llm" | "mail" | "prompt" | "budget" | "ui" | "evidence" | "audit";
const VALID_TABS: ReadonlySet<TabKey> = new Set([
  "llm",
  "mail",
  "prompt",
  "budget",
  "ui",
  "evidence",
  "audit",
]);

const TAB_TITLES: Record<TabKey, string> = {
  llm: "LLM Bağlantıları",
  mail: "Mail Ayarları",
  prompt: "Sistem Prompt",
  budget: "Bütçe",
  ui: "Arayüz",
  evidence: "Kanıt Belgeleri",
  audit: "Audit Log",
};

export function Settings(): JSX.Element {
  const { section } = useParams<{ section: string }>();
  const activeTab: TabKey = VALID_TABS.has(section as TabKey) ? (section as TabKey) : "llm";
  const [settings, setSettings] = useState<SettingListResponse["settings"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    api<SettingListResponse>("/admin/settings")
      .then((res) => {
        if (!aborted) setSettings(res.settings);
      })
      .catch((err) => {
        if (!aborted) setError(err instanceof Error ? err.message : "Ayarlar yüklenemedi");
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, []);

  const groupEntries =
    activeTab === "audit" ? [] : SETTING_REGISTRY.filter((e) => e.group === activeTab);
  const getValue = (key: string): string | null =>
    settings?.find((s) => s.key === key)?.value ?? null;
  const getHasValue = (key: string): boolean =>
    settings?.find((s) => s.key === key)?.hasValue ?? false;

  const saveSetting = async (key: string, value: string): Promise<void> => {
    await api("/admin/settings", { method: "PUT", body: { key, value } });
    const fresh = await api<SettingListResponse>("/admin/settings");
    setSettings(fresh.settings);
  };

  return (
    <div className="p-8" style={{ fontFamily: "var(--font-sans)", maxWidth: 1400 }}>
      <header className="mb-6">
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.72rem",
            letterSpacing: "0.08em",
            color: "var(--color-text-3)",
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          ADMİN · Sistem Ayarları
        </div>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.8rem",
            color: "var(--color-text)",
          }}
        >
          {TAB_TITLES[activeTab]}
        </h1>
        <HealthStrip />
      </header>

      <main
        style={{
          padding: 24,
          minHeight: 480,
          minWidth: 0,
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          borderRadius: 12,
        }}
      >
        {loading && <p style={{ color: "var(--color-text-3)" }}>Yükleniyor…</p>}
        {error && (
          <div
            className="px-4 py-2 rounded text-sm"
            style={{
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
            }}
          >
            {error}
          </div>
        )}
        {!loading && !error && activeTab !== "audit" && (
          <>
            <SettingsForm
              entries={groupEntries}
              getValue={getValue}
              getHasValue={getHasValue}
              onSave={saveSetting}
            />
            {activeTab === "llm" && <LLMTestPanel />}
            {activeTab === "mail" && <MailTestPanel />}
            {activeTab === "prompt" && <PromptPreviewPanel />}
            {activeTab === "evidence" && <EvidencePolicyPanel />}
          </>
        )}
        {activeTab === "audit" && <AuditPanel />}
      </main>
    </div>
  );
}

function LLMTestPanel(): JSX.Element {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<LLMTestResponse | null>(null);

  const runTest = async (): Promise<void> => {
    setTesting(true);
    setResult(null);
    try {
      const res = await api<LLMTestResponse>("/admin/settings/llm/test", { method: "POST" });
      setResult(res);
    } catch (err) {
      setResult({
        success: false,
        provider: "unknown",
        model: "",
        errorMessage: err instanceof Error ? err.message : "Bilinmeyen hata",
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="mt-6 pt-6" style={{ borderTop: "1px solid var(--color-border)" }}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "1rem",
              color: "var(--color-text)",
            }}
          >
            Bağlantı Testi
          </h3>
          <p className="mt-1" style={{ fontSize: "0.78rem", color: "var(--color-text-3)" }}>
            Aktif provider'a 5-token küçük prompt gönderir (~$0.0001). Key doğruluğunu +
            erişilebilirliği doğrular.
          </p>
        </div>
        <button
          onClick={() => void runTest()}
          disabled={testing}
          className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
          style={{
            background: "var(--color-primary)",
            color: "var(--color-surface)",
            border: "none",
            cursor: testing ? "not-allowed" : "pointer",
          }}
        >
          {testing ? "Test ediliyor…" : "Test Et"}
        </button>
      </div>
      {result && (
        <div
          data-alert-variant={result.success ? "success" : "error"}
          className="mt-3 px-4 py-3 rounded-lg text-sm"
          style={{
            background: result.success
              ? "color-mix(in srgb, var(--color-success) 10%, white)"
              : "color-mix(in srgb, var(--color-error) 10%, white)",
            color: result.success ? "var(--color-success)" : "var(--color-error)",
            border: "1px solid",
            borderColor: result.success
              ? "color-mix(in srgb, var(--color-success) 30%, white)"
              : "color-mix(in srgb, var(--color-error) 35%, white)",
          }}
        >
          {result.success ? (
            <>
              <strong>Başarılı</strong> — {result.provider} / {result.model} · {result.latencyMs}
              ms
            </>
          ) : (
            <>
              <strong>✗ Hata</strong> — {result.provider}{" "}
              {result.errorCode ? `(${result.errorCode})` : ""}: {result.errorMessage}
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface LayerCardProps {
  label: string;
  color: string;
  content: string;
  emptyHint?: string;
}

function LayerCard({ label, color, content, emptyHint }: LayerCardProps): JSX.Element {
  const isEmpty = content.trim().length === 0;
  return (
    <div
      style={{
        border: "1px solid var(--color-border)",
        borderLeft: `3px solid ${isEmpty ? "var(--color-border)" : color}`,
        borderRadius: 6,
        background: "var(--color-surface)",
        marginBottom: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "6px 10px",
          borderBottom: isEmpty ? "none" : "1px solid var(--color-border)",
        }}
      >
        <strong style={{ fontSize: "0.78rem", color: "var(--color-text)" }}>{label}</strong>
        {isEmpty ? (
          <span
            style={{
              fontSize: "0.65rem",
              padding: "1px 6px",
              borderRadius: 3,
              background: "var(--color-bg)",
              color: "var(--color-text-3)",
            }}
          >
            BOŞ
          </span>
        ) : (
          <span style={{ fontSize: "0.68rem", color: "var(--color-text-3)" }}>
            {content.length} kar.
          </span>
        )}
      </div>
      {!isEmpty ? (
        <pre
          style={{
            margin: 0,
            padding: "8px 10px",
            fontFamily: "var(--font-mono)",
            fontSize: "0.74rem",
            whiteSpace: "pre-wrap",
            color: "var(--color-text-2)",
            background: "var(--color-bg)",
            maxHeight: 220,
            overflowY: "auto",
          }}
        >
          {content}
        </pre>
      ) : emptyHint ? (
        <p
          style={{
            margin: 0,
            padding: "6px 10px",
            fontSize: "0.72rem",
            color: "var(--color-text-3)",
            fontStyle: "italic",
          }}
        >
          {emptyHint}
        </p>
      ) : null}
    </div>
  );
}

function PromptPreviewPanel(): JSX.Element {
  const [projects, setProjects] = useState<ProjectsListResponse | null>(null);
  const [projectId, setProjectId] = useState<string>("");
  const [snapshot, setSnapshot] = useState<ProjectSnapshotResponse | null>(null);
  const [sectionCode, setSectionCode] = useState<string>("");
  const [isolateQuestion, setIsolateQuestion] = useState<boolean>(false);
  const [questionCode, setQuestionCode] = useState<string>("");
  const [preview, setPreview] = useState<PromptPreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Proje listesini yükle (ilk önizleme aşağıdaki seçim effect'inden gelir — sadece Layer 1)
  useEffect(() => {
    void api<ProjectsListResponse>("/projects?pageSize=100&page=1")
      .then(setProjects)
      .catch(() => {
        /* yetkili olmayan kullanıcılarda 403 sessizce */
      });
  }, []);

  // Proje seçilince snapshot çek (section/question dropdownlarını doldurmak için)
  useEffect(() => {
    if (!projectId) {
      setSnapshot(null);
      setSectionCode("");
      setQuestionCode("");
      setIsolateQuestion(false);
      return;
    }
    void api<ProjectSnapshotResponse>(`/projects/${projectId}/snapshot`)
      .then((s) => {
        setSnapshot(s);
        setSectionCode("");
        setQuestionCode("");
        setIsolateQuestion(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Snapshot alınamadı");
      });
  }, [projectId]);

  const effectiveQuestion = isolateQuestion ? questionCode : "";
  const run = useCallback(async (): Promise<void> => {
    setError(null);
    setLoading(true);
    const body: Record<string, string> = {};
    if (projectId) body.projectId = projectId;
    if (sectionCode) body.sectionCode = sectionCode;
    if (effectiveQuestion) body.questionCode = effectiveQuestion;
    try {
      const res = await api<PromptPreviewResponse>("/admin/prompt/preview", {
        method: "POST",
        body,
      });
      setPreview(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Preview alınamadı");
    } finally {
      setLoading(false);
    }
  }, [projectId, sectionCode, effectiveQuestion]);

  // TODO-KPI-24: proje / section / soru seçimi değişince önizleme otomatik yenilenir ("Yenile" durur).
  useEffect(() => {
    void run();
  }, [run]);

  const availableQuestions =
    snapshot && sectionCode
      ? snapshot.snapshot.questions.filter((q) => {
          const s = snapshot.snapshot.sections.find((sec) => sec.code === sectionCode);
          return s?.id === q.sectionId;
        })
      : [];

  return (
    <div className="mt-6 pt-6" style={{ borderTop: "1px solid var(--color-border)" }}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "1rem",
              color: "var(--color-text)",
            }}
          >
            Assembled Preview (§8.4) — 4 Katman
          </h3>
          <p className="mt-1" style={{ fontSize: "0.78rem", color: "var(--color-text-3)" }}>
            LLM'e gidecek prompt'un katman katman dökümü. Proje + section seçince Layer 2-4 + user
            response dolar; sadece Layer 1 görmek için boş bırak.
          </p>
        </div>
        <button
          onClick={() => void run()}
          disabled={loading}
          className="px-3 py-1.5 rounded text-xs"
          style={{
            background: "transparent",
            border: "1px solid var(--color-border)",
            color: "var(--color-primary)",
            cursor: loading ? "wait" : "pointer",
          }}
        >
          {loading ? "Yükleniyor…" : "Yenile"}
        </button>
      </div>

      {/* Bağlam seçici */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          marginBottom: 12,
          padding: 10,
          background: "var(--color-bg)",
          border: "1px solid var(--color-border)",
          borderRadius: 6,
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: "0.72rem", color: "var(--color-text-3)" }}>Proje</span>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            style={{
              padding: "4px 8px",
              fontSize: "0.82rem",
              borderRadius: 4,
              border: "1px solid var(--color-border)",
              background: "var(--color-surface)",
              color: "var(--color-text)",
            }}
          >
            <option value="">— seç (boşsa sadece Layer 1) —</option>
            {projects?.projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.clientName} · {p.frameworkName} {p.versionNumber} ·{" "}
                {formatReportingPeriod(p.reportingYear, p.periodType)}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: "0.72rem", color: "var(--color-text-3)" }}>
            Section{" "}
            <span style={{ color: "var(--color-text-3)", fontWeight: 400 }}>
              (opsiyonel — boşsa sadece Layer 1+2)
            </span>
          </span>
          <select
            value={sectionCode}
            onChange={(e) => {
              setSectionCode(e.target.value);
              setQuestionCode("");
              setIsolateQuestion(false);
            }}
            disabled={!snapshot}
            style={{
              padding: "4px 8px",
              fontSize: "0.82rem",
              borderRadius: 4,
              border: "1px solid var(--color-border)",
              background: snapshot ? "var(--color-surface)" : "var(--color-bg)",
              color: "var(--color-text)",
            }}
          >
            <option value="">— seç —</option>
            {snapshot?.snapshot.sections.map((s) => (
              <option key={s.code} value={s.code}>
                {s.code} · {s.label}
              </option>
            ))}
          </select>
        </label>
        <label
          style={{
            gridColumn: "1 / -1",
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: "0.78rem",
            color: "var(--color-text-2)",
            opacity: sectionCode ? 1 : 0.5,
          }}
        >
          <input
            type="checkbox"
            checked={isolateQuestion}
            onChange={(e) => setIsolateQuestion(e.target.checked)}
            disabled={!sectionCode}
          />
          <span>Tek soru ile izole et (debug — gerçek üretim section bazında çalışır)</span>
          {isolateQuestion && sectionCode ? (
            <select
              value={questionCode}
              onChange={(e) => setQuestionCode(e.target.value)}
              style={{
                padding: "2px 6px",
                fontSize: "0.78rem",
                borderRadius: 4,
                border: "1px solid var(--color-border)",
                background: "var(--color-surface)",
                color: "var(--color-text)",
              }}
            >
              <option value="">— soru —</option>
              {availableQuestions.map((q) => (
                <option key={q.code} value={q.code}>
                  {q.code} · {q.label}
                </option>
              ))}
            </select>
          ) : null}
        </label>
      </div>

      {error && (
        <div
          className="px-4 py-2 rounded text-sm mb-3"
          style={{
            background: "color-mix(in srgb, var(--color-error) 10%, white)",
            color: "var(--color-error)",
          }}
        >
          {error}
        </div>
      )}

      {preview && (
        <>
          <LayerCard
            label="🔵 Layer 1 — System Prompt (global)"
            color="#3b82f6"
            content={preview.systemLayer}
            emptyHint="Sistem prompt boş. /admin/settings'te 'Sistem Prompt' alanını doldur."
          />
          <LayerCard
            label="🟣 Layer 2 — Çerçeve Prompt'u (placeholder resolve edilmiş)"
            color="#a855f7"
            content={preview.frameworkLayer}
            emptyHint={
              projectId
                ? "Bu projenin çerçevesinin Layer 2 prompt'u boş. Çerçeve Yöneticisi'nde üst karta yaz."
                : "Proje seçilmedi."
            }
          />
          <LayerCard
            label="🟢 Layer 3 — Section Prompt"
            color="#22c55e"
            content={preview.sectionLayer}
            emptyHint={
              sectionCode
                ? "Bu section'ın Layer 3 prompt'u boş. Section editor'da 'Bölüm Prompt' alanını doldur."
                : "Section seçilmedi."
            }
          />
          <LayerCard
            label={
              isolateQuestion && questionCode
                ? "🟠 Layer 4 — Question aiContext (tek soru — debug)"
                : "🟠 Layer 4 — Question aiContexts (section'daki tüm sorular concat)"
            }
            color="#f97316"
            content={preview.questionLayer}
            emptyHint={
              sectionCode
                ? "Bu section'daki hiçbir soruda aiContext yok. Question editor'larda 'AI Bağlam Yönergesi'ni doldur."
                : "Section seçilmedi."
            }
          />
          <LayerCard
            label="⚫ User Response Block (form yanıtları)"
            color="#525252"
            content={preview.userResponseBlock}
            emptyHint={
              sectionCode ? "Bu section'da henüz form yanıtı girilmemiş." : "Section seçilmedi."
            }
          />
          <LayerCard
            label="🟤 KPI Tablosu (=== KPI TABLOSU === — projenin KPI veri seti)"
            color="#8b5a2b"
            content={preview.kpiBlock ?? ""}
            emptyHint={
              projectId
                ? "Bu projeye KPI veri seti bağlı değil (ya da çeyreklik proje) — blok eklenmez. Proje detayından 'Veri seti bağla'."
                : "Proje seçilmedi."
            }
          />
          <details
            style={{
              border: "1px solid var(--color-border)",
              borderRadius: 6,
              padding: "6px 10px",
              marginTop: 8,
            }}
          >
            <summary style={{ cursor: "pointer", fontSize: "0.82rem", color: "var(--color-text)" }}>
              📄 Final assembled — LLM'e giden full text ({preview.fullText.length} kar.)
            </summary>
            <pre
              style={{
                marginTop: 8,
                fontFamily: "var(--font-mono)",
                fontSize: "0.74rem",
                whiteSpace: "pre-wrap",
                color: "var(--color-text-2)",
                background: "var(--color-bg)",
                padding: 10,
                borderRadius: 4,
                maxHeight: 400,
                overflowY: "auto",
              }}
            >
              {preview.fullText || "(boş)"}
            </pre>
          </details>
          {Object.keys(preview.resolvedPlaceholders).some(
            (k) => preview.resolvedPlaceholders[k],
          ) ? (
            <details
              style={{
                border: "1px solid var(--color-border)",
                borderRadius: 6,
                padding: "6px 10px",
                marginTop: 6,
              }}
            >
              <summary
                style={{ cursor: "pointer", fontSize: "0.82rem", color: "var(--color-text)" }}
              >
                🔖 Resolve edilmiş placeholder değerleri
              </summary>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: "8px 0 0",
                  fontSize: "0.76rem",
                }}
              >
                {Object.entries(preview.resolvedPlaceholders).map(([k, v]) => (
                  <li key={k} style={{ marginBottom: 2 }}>
                    <code
                      style={{
                        fontFamily: "var(--font-mono)",
                        color: "var(--color-primary)",
                      }}
                    >
                      {`{{${k}}}`}
                    </code>{" "}
                    →{" "}
                    <span style={{ color: v ? "var(--color-text-2)" : "var(--color-text-3)" }}>
                      {v || "(boş)"}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </>
      )}

      <div className="mt-4">
        <h4 style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--color-text)" }}>
          Çerçeve / Bölüm / Soru prompt'larında kullanılabilir placeholder'lar
        </h4>
        <p style={{ fontSize: "0.72rem", color: "var(--color-text-3)", marginTop: 2 }}>
          Sistem Prompt'ta kullanmayın — orada resolve edilmez.
        </p>
        <ul
          className="mt-2"
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            fontSize: "0.78rem",
            color: "var(--color-text-2)",
          }}
        >
          {PLACEHOLDER_CATALOG.map((p) => (
            <li key={p.token} style={{ marginBottom: 4 }}>
              <code style={{ fontFamily: "var(--font-mono)", color: "var(--color-primary)" }}>
                {p.token}
              </code>{" "}
              — {p.description} <span style={{ color: "var(--color-text-3)" }}>({p.scope})</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function MailTestPanel(): JSX.Element {
  const [testing, setTesting] = useState(false);
  const [to, setTo] = useState("");
  const [result, setResult] = useState<MailTestResponse | null>(null);

  const runTest = async (): Promise<void> => {
    setTesting(true);
    setResult(null);
    try {
      const res = await api<MailTestResponse>("/admin/settings/mail/test", {
        method: "POST",
        body: to ? { to } : {},
      });
      setResult(res);
    } catch (err) {
      setResult({
        success: false,
        to,
        errorMessage: err instanceof Error ? err.message : "Bilinmeyen hata",
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="mt-6 pt-6" style={{ borderTop: "1px solid var(--color-border)" }}>
      <h3
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "1rem",
          color: "var(--color-text)",
        }}
      >
        Test Mail Gönder
      </h3>
      <p className="mt-1 mb-3" style={{ fontSize: "0.78rem", color: "var(--color-text-3)" }}>
        SMTP bağlantısını doğrular + test içeriği yollar. Alıcı boş bırakılırsa giriş yapan admin'e
        gider.
      </p>
      <div className="flex items-center gap-2">
        <input
          type="email"
          value={to}
          placeholder="alıcı@ornek.com (opsiyonel)"
          onChange={(e) => setTo(e.target.value)}
          className="flex-1 rounded px-3 py-1.5 text-sm"
          style={{
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            color: "var(--color-text)",
          }}
        />
        <button
          onClick={() => void runTest()}
          disabled={testing}
          className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
          style={{
            background: "var(--color-primary)",
            color: "var(--color-surface)",
            border: "none",
            cursor: testing ? "not-allowed" : "pointer",
          }}
        >
          {testing ? "Gönderiliyor…" : "Test Mail Gönder"}
        </button>
      </div>
      {result && (
        <div
          className="mt-3 px-4 py-3 rounded-lg text-sm"
          style={{
            background: result.success
              ? "color-mix(in srgb, var(--color-success) 10%, white)"
              : "color-mix(in srgb, var(--color-error) 10%, white)",
            color: result.success ? "var(--color-success)" : "var(--color-error)",
            border: "1px solid",
            borderColor: result.success
              ? "color-mix(in srgb, var(--color-success) 30%, white)"
              : "color-mix(in srgb, var(--color-error) 35%, white)",
          }}
        >
          {result.success ? (
            <>
              <strong>✓ Gönderildi</strong> — {result.to}
              {result.latencyMs ? ` · ${result.latencyMs}ms` : ""}
            </>
          ) : (
            <>
              <strong>✗ Hata</strong> — {result.errorMessage}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// M8-S2 — audit log UI: search + filter (action/entityType/date) + pagination
const AUDIT_ACTION_OPTIONS = [
  { value: "", label: "Tüm aksiyonlar" },
  { value: "auth.login.success", label: "auth.login.success" },
  { value: "auth.login.failure", label: "auth.login.failure" },
  { value: "auth.login.locked", label: "auth.login.locked" },
  { value: "auth.logout", label: "auth.logout" },
  { value: "auth.password_reset.requested", label: "auth.password_reset.requested" },
  { value: "auth.first_login.completed", label: "auth.first_login.completed" },
  { value: "auth.session.invalidated", label: "auth.session.invalidated" },
  { value: "settings.update", label: "settings.update" },
  { value: "llm.test", label: "llm.test" },
  { value: "framework.create", label: "framework.create" },
  { value: "framework.update", label: "framework.update" },
  { value: "framework.publish", label: "framework.publish" },
  { value: "framework.version_fork", label: "framework.version_fork" },
  { value: "framework.section.create", label: "framework.section.create" },
  { value: "framework.section.update", label: "framework.section.update" },
  { value: "framework.section.delete", label: "framework.section.delete" },
  { value: "framework.question.create", label: "framework.question.create" },
  { value: "framework.question.update", label: "framework.question.update" },
  { value: "framework.question.delete", label: "framework.question.delete" },
  { value: "form_response.batch_upsert", label: "form_response.batch_upsert" },
  { value: "form_response.stale_write_rejected", label: "form_response.stale_write_rejected" },
  { value: "section.review.approve", label: "section.review.approve" },
  { value: "section.review.reopen", label: "section.review.reopen" },
  { value: "project.reopen", label: "project.reopen" },
  { value: "document.export", label: "document.export" },
];

const AUDIT_ENTITY_OPTIONS = [
  { value: "", label: "Tüm varlıklar" },
  { value: "user", label: "user" },
  { value: "client", label: "client" },
  { value: "project", label: "project" },
  { value: "framework", label: "framework" },
  { value: "section", label: "section" },
  { value: "question", label: "question" },
  { value: "form_response", label: "form_response" },
  { value: "role", label: "role" },
  { value: "settings", label: "settings" },
  { value: "mail", label: "mail" },
  { value: "llm", label: "llm" },
  { value: "document", label: "document" },
];

function AuditPanel(): JSX.Element {
  const [data, setData] = useState<AuditLogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // debounce serbest metin arama
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    const qs = new URLSearchParams();
    if (search) qs.set("search", search);
    if (action) qs.set("action", action);
    if (entityType) qs.set("entityType", entityType);
    if (dateFrom) qs.set("dateFrom", new Date(dateFrom).toISOString());
    if (dateTo) qs.set("dateTo", new Date(dateTo).toISOString());
    qs.set("page", String(page));
    qs.set("pageSize", String(pageSize));
    api<AuditLogResponse>(`/admin/settings/audit?${qs.toString()}`)
      .then((res) => {
        if (!aborted) setData(res);
      })
      .catch((err: Error) => {
        if (!aborted) setError(err.message);
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [search, action, entityType, dateFrom, dateTo, page]);

  const clearFilters = (): void => {
    setSearchInput("");
    setSearch("");
    setAction("");
    setEntityType("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const hasFilters = search || action || entityType || dateFrom || dateTo;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div>
      <p className="mb-3" style={{ fontSize: "0.82rem", color: "var(--color-text-2)" }}>
        Audit log append-only — silinmez, değiştirilemez (DB trigger ile korunur). Arama
        action/entity/metadata/kullanıcı alanlarında çalışır.
      </p>

      {/* Filter bar */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr 1fr 1fr 1fr auto",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <input
          type="text"
          placeholder="Ara (metadata, aksiyon, e-posta, isim)…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{
            padding: "6px 10px",
            border: "1px solid var(--color-border)",
            borderRadius: 6,
            fontSize: "0.85rem",
          }}
        />
        <select
          value={action}
          onChange={(e) => {
            setAction(e.target.value);
            setPage(1);
          }}
          style={{
            padding: "6px 10px",
            border: "1px solid var(--color-border)",
            borderRadius: 6,
            fontSize: "0.85rem",
            background: "var(--color-surface)",
          }}
        >
          {AUDIT_ACTION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={entityType}
          onChange={(e) => {
            setEntityType(e.target.value);
            setPage(1);
          }}
          style={{
            padding: "6px 10px",
            border: "1px solid var(--color-border)",
            borderRadius: 6,
            fontSize: "0.85rem",
            background: "var(--color-surface)",
          }}
        >
          {AUDIT_ENTITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setPage(1);
          }}
          placeholder="Başlangıç"
          title="Başlangıç tarihi"
          style={{
            padding: "6px 10px",
            border: "1px solid var(--color-border)",
            borderRadius: 6,
            fontSize: "0.85rem",
          }}
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setPage(1);
          }}
          placeholder="Bitiş"
          title="Bitiş tarihi"
          style={{
            padding: "6px 10px",
            border: "1px solid var(--color-border)",
            borderRadius: 6,
            fontSize: "0.85rem",
          }}
        />
        <button
          onClick={clearFilters}
          disabled={!hasFilters}
          style={{
            padding: "6px 10px",
            border: "1px solid var(--color-border)",
            borderRadius: 6,
            background: hasFilters ? "var(--color-surface)" : "var(--color-bg)",
            color: hasFilters ? "var(--color-text)" : "var(--color-text-3)",
            fontSize: "0.82rem",
            cursor: hasFilters ? "pointer" : "not-allowed",
          }}
        >
          Temizle
        </button>
      </div>

      {error && (
        <div
          className="px-4 py-2 rounded text-sm mb-3"
          style={{
            background: "color-mix(in srgb, var(--color-error) 10%, white)",
            color: "var(--color-error)",
          }}
        >
          {error}
        </div>
      )}

      {loading && !data && <p style={{ color: "var(--color-text-3)" }}>Yükleniyor…</p>}

      {data?.entries.length === 0 && (
        <p style={{ color: "var(--color-text-3)" }}>
          {hasFilters
            ? "Filtrelere uyan kayıt yok. Filtreleri temizleyin."
            : "Henüz audit kaydı yok. Auth, user, settings, form aksiyonları burada listelenir."}
        </p>
      )}

      {data && data.entries.length > 0 && (
        <>
          <div
            className="rounded-lg"
            style={{ border: "1px solid var(--color-border)", overflowX: "auto" }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
              <thead>
                <tr style={{ background: "var(--color-bg)" }}>
                  <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600 }}>Zaman</th>
                  <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600 }}>Kim</th>
                  <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600 }}>
                    Aksiyon
                  </th>
                  <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600 }}>Hedef</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e) => (
                  <tr key={e.id} style={{ borderTop: "1px solid var(--color-border)" }}>
                    <td
                      style={{
                        padding: "6px 10px",
                        fontFamily: "var(--font-mono)",
                        fontSize: "0.75rem",
                        color: "var(--color-text-3)",
                      }}
                    >
                      {new Date(e.createdAt).toLocaleString("tr-TR")}
                    </td>
                    <td style={{ padding: "6px 10px" }}>{e.actorName ?? e.actorEmail ?? "—"}</td>
                    <td
                      style={{
                        padding: "6px 10px",
                        fontFamily: "var(--font-mono)",
                        fontSize: "0.75rem",
                      }}
                    >
                      {e.action}
                    </td>
                    <td
                      style={{
                        padding: "6px 10px",
                        color: "var(--color-text-3)",
                        maxWidth: 280,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={e.entityId ? `${e.entityType} · ${e.entityId}` : e.entityType}
                    >
                      {e.entityType}
                      {e.entityId ? ` · ${e.entityId.slice(0, 8)}…` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 10,
              fontSize: "0.82rem",
              color: "var(--color-text-2)",
            }}
          >
            <span>
              Toplam {data.total} kayıt · Sayfa {data.page}/{totalPages}
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                style={{
                  padding: "4px 10px",
                  border: "1px solid var(--color-border)",
                  borderRadius: 6,
                  background: page > 1 ? "var(--color-surface)" : "var(--color-bg)",
                  color: page > 1 ? "var(--color-text)" : "var(--color-text-3)",
                  cursor: page > 1 ? "pointer" : "not-allowed",
                  fontSize: "0.82rem",
                }}
              >
                ← Önceki
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                style={{
                  padding: "4px 10px",
                  border: "1px solid var(--color-border)",
                  borderRadius: 6,
                  background: page < totalPages ? "var(--color-surface)" : "var(--color-bg)",
                  color: page < totalPages ? "var(--color-text)" : "var(--color-text-3)",
                  cursor: page < totalPages ? "pointer" : "not-allowed",
                  fontSize: "0.82rem",
                }}
              >
                Sonraki →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SettingsForm({
  entries,
  getValue,
  getHasValue,
  onSave,
}: {
  entries: SettingRegistryEntry[];
  getValue: (key: string) => string | null;
  getHasValue: (key: string) => boolean;
  onSave: (key: string, value: string) => Promise<void>;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      {entries.map((entry) => (
        <SettingField
          key={entry.key}
          entry={entry}
          initial={getValue(entry.key) ?? ""}
          hasValue={getHasValue(entry.key)}
          onSave={(v) => onSave(entry.key, v)}
        />
      ))}
    </div>
  );
}

function SettingField({
  entry,
  initial,
  hasValue,
  onSave,
}: {
  entry: SettingRegistryEntry;
  initial: string;
  hasValue: boolean;
  onSave: (value: string) => Promise<void>;
}): JSX.Element {
  // Non-secret: input/textarea açılışta gerçek değeri gösterir (state init).
  // Secret: input boş, "Mevcut: ***" satırı yukarıda — yeni değer yaz pattern'i.
  const showInitialInInput = !entry.isSecret;
  const [value, setValue] = useState(showInitialInInput ? initial : "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Parent /admin/settings re-fetch ettiğinde initial güncellenir — value'yu da senkronla.
  // Secret için useEffect setValue("") yapar (mevcut maskelenmiş değer Mevcut: satırında görünür).
  useEffect(() => {
    setValue(showInitialInInput ? initial : "");
  }, [initial, showInitialInInput]);

  const isBool = entry.type === "boolean";
  const isMultiline =
    entry.key === "prompt.system" ||
    entry.key === "llm.pricing" ||
    entry.key === "prompt.report_check";
  // Sistem prompt globaldir; placeholder resolve edilmez. Onun yerine yönlendirme notu gösterilir.
  const showSystemPromptNotice = entry.key === "prompt.system";
  // Save disabled when no change (non-secret) or empty (secret).
  const isDirty = showInitialInInput ? value !== initial : value.length > 0;

  return (
    <div>
      <label
        style={{
          display: "block",
          fontSize: "0.82rem",
          fontWeight: 500,
          color: "var(--color-text)",
          marginBottom: 4,
        }}
      >
        {entry.label}
        {entry.isSecret && (
          <span
            style={{
              marginLeft: 8,
              padding: "1px 6px",
              borderRadius: 4,
              background: "var(--color-bg)",
              color: "var(--color-error)",
              fontSize: "0.65rem",
              fontWeight: 600,
            }}
          >
            SECRET
          </span>
        )}
      </label>
      <p
        style={{
          fontSize: "0.75rem",
          color: "var(--color-text-3)",
          marginBottom: 6,
        }}
      >
        {entry.description}
      </p>
      {hasValue && entry.isSecret && (
        <p
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.78rem",
            color: "var(--color-text-2)",
            marginBottom: 4,
          }}
        >
          Mevcut: {initial}{" "}
          <span style={{ color: "var(--color-text-3)" }}>
            (değiştirmek için yeni değer yaz + Kaydet)
          </span>
        </p>
      )}
      {showSystemPromptNotice && (
        <div
          style={{
            marginBottom: 8,
            padding: "8px 10px",
            borderRadius: 6,
            background: "var(--color-bg)",
            border: "1px solid var(--color-border)",
            borderLeft: "3px solid var(--color-primary)",
            fontSize: "0.75rem",
            color: "var(--color-text-2)",
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: "var(--color-text)" }}>Global katman.</strong> Bu prompt her
          çerçeve/proje için aynı kalır ve placeholder resolve edilmez. Dinamik bilgi (şirket adı,
          sektör, yıl) için <strong>Çerçeve / Bölüm / Soru</strong> prompt'larında{" "}
          <code style={{ fontFamily: "var(--font-mono)" }}>{"{{company_name}}"}</code>,{" "}
          <code style={{ fontFamily: "var(--font-mono)" }}>{"{{reporting_year}}"}</code> gibi
          token'ları kullanın.
        </div>
      )}
      <div
        className={isMultiline ? "flex flex-col gap-2" : "flex items-center gap-2"}
        style={isMultiline ? { alignItems: "stretch" } : {}}
      >
        {isBool ? (
          <select
            aria-label={entry.label ?? entry.key}
            value={value || "false"}
            onChange={(e) => setValue(e.target.value)}
            className="rounded px-2 py-1.5 text-sm"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              color: "var(--color-text)",
            }}
          >
            <option value="true">Açık</option>
            <option value="false">Kapalı</option>
          </select>
        ) : isMultiline ? (
          <textarea
            ref={textareaRef}
            aria-label={entry.label ?? entry.key}
            value={value}
            placeholder={entry.isSecret ? "(gizli — yeni değer yaz)" : ""}
            onChange={(e) => setValue(e.target.value)}
            rows={entry.key === "prompt.system" ? 10 : 8}
            className="rounded px-3 py-2 text-sm"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              color: "var(--color-text)",
              fontFamily: entry.key === "llm.pricing" ? "var(--font-mono)" : "inherit",
              fontSize: entry.key === "llm.pricing" ? "0.78rem" : "0.85rem",
              resize: "vertical",
              minHeight: 140,
              width: "100%",
            }}
          />
        ) : (
          <input
            aria-label={entry.label ?? entry.key}
            type={entry.isSecret ? "password" : entry.type === "number" ? "number" : "text"}
            value={value}
            placeholder={entry.isSecret ? "(gizli — yeni değer yaz)" : ""}
            onChange={(e) => setValue(e.target.value)}
            className="flex-1 rounded px-3 py-1.5 text-sm"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              color: "var(--color-text)",
            }}
          />
        )}
        <button
          onClick={() => {
            if (!isDirty) return;
            setSaving(true);
            setSaveError(null);
            onSave(value)
              .then(() => {
                setSaved(true);
                // Secret için input'u temizle (mevcut maskelenmiş değer Mevcut: satırında görünür).
                // Non-secret için value korunur — useEffect yeni initial geldiğinde sync eder.
                if (entry.isSecret) setValue("");
                setTimeout(() => setSaved(false), 2000);
              })
              .catch((err: Error) => setSaveError(err.message))
              .finally(() => setSaving(false));
          }}
          disabled={!isDirty || saving}
          className="px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-50"
          style={{
            background: "var(--color-primary)",
            color: "var(--color-surface)",
            border: "none",
            cursor: isDirty && !saving ? "pointer" : "not-allowed",
          }}
        >
          {saving ? "Kaydediliyor…" : saved ? "✓ Kaydedildi" : "Kaydet"}
        </button>
      </div>
      {saveError && (
        <p style={{ color: "var(--color-error)", fontSize: "0.75rem", marginTop: 4 }}>
          {saveError}
        </p>
      )}
    </div>
  );
}

// M8-S3 — 4-kanal health strip (api / db / storage / llm)
type HealthStatus = "ok" | "degraded" | "down";
interface HealthCheck {
  ok: boolean;
  service: string;
  status: HealthStatus;
  latencyMs?: number;
  message?: string;
}
interface HealthAllResponse {
  overall: HealthStatus;
  checks: { api: HealthCheck; db: HealthCheck; storage: HealthCheck; llm: HealthCheck };
}

const STATUS_LABEL: Record<HealthStatus, string> = {
  ok: "Çalışıyor",
  degraded: "Sınırlı",
  down: "Devre dışı",
};
const STATUS_COLOR: Record<HealthStatus, { bg: string; fg: string }> = {
  ok: { bg: "color-mix(in srgb, var(--color-success) 10%, white)", fg: "var(--color-success)" },
  degraded: {
    bg: "color-mix(in srgb, var(--color-warning) 14%, white)",
    fg: "var(--color-warning)",
  },
  down: { bg: "color-mix(in srgb, var(--color-error) 8%, white)", fg: "var(--color-error)" },
};
const CHANNEL_LABEL: Record<keyof HealthAllResponse["checks"], string> = {
  api: "API",
  db: "Veritabanı",
  storage: "Depolama",
  llm: "LLM",
};

function HealthStrip(): JSX.Element {
  const [data, setData] = useState<HealthAllResponse | null>(null);

  useEffect(() => {
    let aborted = false;
    const load = async (): Promise<void> => {
      try {
        // /health/all prefix'siz de expose edilmiş — ama api client /api/v1 prefix'i ekliyor;
        // orta yol: ayrı fetch ile /health/all çağır
        const res = await fetch("/health/all", { credentials: "include" });
        if (!res.ok) return;
        const json = (await res.json()) as HealthAllResponse;
        if (!aborted) setData(json);
      } catch {
        /* no-op */
      }
    };
    void load();
    const id = setInterval(() => void load(), 30_000);
    return () => {
      aborted = true;
      clearInterval(id);
    };
  }, []);

  if (!data) {
    return (
      <div style={{ marginTop: 8, fontSize: "0.75rem", color: "var(--color-text-3)" }}>
        Sistem durumu kontrol ediliyor…
      </div>
    );
  }

  const channels = Object.entries(data.checks) as [
    keyof HealthAllResponse["checks"],
    HealthCheck,
  ][];

  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        marginTop: 8,
        marginBottom: 4,
        flexWrap: "wrap",
      }}
    >
      {channels.map(([key, check]) => {
        const color = STATUS_COLOR[check.status];
        return (
          <div
            key={key}
            title={check.message ?? STATUS_LABEL[check.status]}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 999,
              background: color.bg,
              color: color.fg,
              fontSize: "0.72rem",
              fontWeight: 500,
              border: `1px solid ${color.fg}20`,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                background: color.fg,
              }}
            />
            <span>{CHANNEL_LABEL[key]}</span>
            <span style={{ opacity: 0.8 }}>· {STATUS_LABEL[check.status]}</span>
            {typeof check.latencyMs === "number" && (
              <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", opacity: 0.6 }}>
                {check.latencyMs.toString()}ms
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// M12 — Branding paneli ayrı sayfaya taşındı: /admin/branding (AdminBranding.tsx)
